import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { base44 } from "api/apiClient";
import { useRestaurant } from "../contexts/RestaurantContext";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import {
  Bot,
  Send,
  Loader2,
  Sparkles,
  X,
  Mic,
  MicOff,
  Plus,
  Maximize2,
  ChevronDown,
  FileText,
  Camera,
  Image as ImageIcon,
} from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize from "rehype-sanitize";
import { format } from "date-fns";

const MAX_ATTACHMENT_MB = 30;
const MAX_ATTACHMENTS = 8;
const CHAT_EDGE_GAP = 24;
const CHAT_EDGE_GAP_MOBILE = 16;

// ---------------------------------------------------------------------------
// Markdown components shared by message rendering
// ---------------------------------------------------------------------------
const mdComponents = {
  p: ({ children }) => <p className="my-1.5 leading-relaxed">{children}</p>,
  ul: ({ children }) => (
    <ul className="my-1.5 ml-4 list-disc space-y-0.5">{children}</ul>
  ),
  ol: ({ children }) => (
    <ol className="my-1.5 ml-4 list-decimal space-y-0.5">{children}</ol>
  ),
  li: ({ children }) => <li className="my-0.5">{children}</li>,
  h1: ({ children }) => (
    <h1 className="text-base font-bold my-2">{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className="text-sm font-bold my-1.5">{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className="text-sm font-semibold my-1">{children}</h3>
  ),
  strong: ({ children }) => (
    <strong className="font-semibold text-slate-900">{children}</strong>
  ),
  code: ({ children }) => (
    <code className="px-1 py-0.5 rounded bg-slate-100 text-slate-700 text-xs font-mono">
      {children}
    </code>
  ),
  pre: ({ children }) => {
    const codeChild = React.Children.toArray(children).find(React.isValidElement);
    const codeChildren = codeChild?.props?.children ?? children;

    return (
      <pre className="bg-slate-900 text-slate-100 rounded-lg p-2.5 my-1.5 text-xs font-mono overflow-x-auto">
        <code className="font-mono">{codeChildren}</code>
      </pre>
    );
  },
  table: ({ children }) => (
    <div className="my-2 overflow-x-auto rounded-lg border border-slate-200">
      <table className="min-w-full text-xs">{children}</table>
    </div>
  ),
  thead: ({ children }) => (
    <thead className="bg-slate-50 border-b border-slate-200">{children}</thead>
  ),
  th: ({ children }) => (
    <th className="px-2.5 py-1.5 text-left font-semibold text-slate-700">
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td className="px-2.5 py-1.5 border-t border-slate-100">{children}</td>
  ),
  tr: ({ children }) => <tr className="even:bg-slate-50/50">{children}</tr>,
};

function formatAttachmentSize(size) {
  if (!size || Number.isNaN(size)) return null;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

function getMessageAttachments(metadata = {}) {
  if (Array.isArray(metadata.attachments) && metadata.attachments.length) {
    return metadata.attachments;
  }
  return metadata.attachment?.filename ? [metadata.attachment] : [];
}

function isAttachmentPlaceholderContent(content, attachments = []) {
  if (!content || !attachments.length) return false;
  const names = attachments.map((attachment) => attachment.filename).filter(Boolean).join(", ");
  if (!names) return false;
  return content === `(${names})` || content === `(attached ${names})`;
}

// ---------------------------------------------------------------------------
// Message bubble
// ---------------------------------------------------------------------------
function MessageBubble({ message, t }) {
  const isUser = message.role === "user";
  const attachments = getMessageAttachments(message?.metadata);
  const showMessageContent = message.content && !isAttachmentPlaceholderContent(message.content, attachments);

  return (
    <div className={`flex gap-2.5 ${isUser ? "justify-end" : "justify-start"}`}>
      {!isUser && (
        <div className="h-7 w-7 rounded-lg bg-emerald-700 flex items-center justify-center mt-0.5 flex-shrink-0">
          <Bot className="w-3.5 h-3.5 text-white" />
        </div>
      )}
      <div className={`max-w-[85%] ${isUser ? "flex flex-col items-end" : ""}`}>
        {showMessageContent && (
          <div
            className={`rounded-2xl px-3.5 py-2.5 ${
              isUser
                ? "bg-emerald-700 text-white"
                : "bg-white border border-slate-200 shadow-sm"
            }`}
          >
            {isUser ? (
              <p className="text-sm leading-relaxed">{message.content}</p>
            ) : (
              <ReactMarkdown
                className="text-sm prose prose-sm prose-slate max-w-none [&>*:first-child]:mt-0 [&>*:last-child]:mb-0"
                remarkPlugins={[remarkGfm]}
                rehypePlugins={[rehypeSanitize]}
                components={mdComponents}
              >
                {message.content}
              </ReactMarkdown>
            )}
          </div>
        )}

        {attachments.length > 0 && (
          <div className={`mt-1 flex max-w-full flex-wrap gap-1 ${isUser ? "justify-end" : "justify-start"}`}>
            {attachments.map((attachment, index) => {
              const attachmentSize = formatAttachmentSize(attachment?.size);
              return (
                <div
                  key={`${attachment?.filename || "attachment"}-${index}`}
                  className={`inline-flex max-w-full items-center gap-1.5 rounded-full border px-2.5 py-1 text-[11px] ${
                    isUser
                      ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                      : "border-slate-200 bg-slate-50 text-slate-600"
                  }`}
                >
                  <FileText className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate font-medium">{attachment.filename}</span>
                  {attachmentSize && (
                    <span className={isUser ? "text-emerald-600" : "text-slate-400"}>
                      {attachmentSize}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {message.tool_calls && message.tool_calls.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {message.tool_calls.map((toolCall, idx) => (
              <div
                key={idx}
                className="text-xs bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5"
              >
                <div className="flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3 text-emerald-600" />
                  <span className="font-medium text-slate-600">
                    {toolCall.name?.split(".").pop() || t("carlo.thinking")}
                  </span>
                  {toolCall.status === "completed" && (
                    <Badge className="ml-auto bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0">
                      OK
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Quick action pills
// ---------------------------------------------------------------------------
function QuickActions({ onSelect, t }) {
  const actions = [
    { label: t("carlo.quickLowStock"), prompt: "What's running low?" },
    { label: t("carlo.quickForecast"), prompt: "What's the forecast for today?" },
    { label: t("carlo.quickTasks"), prompt: "Show me pending tasks" },
    { label: t("carlo.quickSales"), prompt: "How were today's sales?" },
  ];

  return (
    <div className="flex flex-wrap gap-1.5 px-1">
      {actions.map((a, i) => (
        <button
          key={i}
          onClick={() => onSelect(a.prompt)}
          className="text-xs px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 transition-colors"
        >
          {a.label}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main CarloChat component
// ---------------------------------------------------------------------------
export default function CarloChat() {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentRestaurant } = useRestaurant();
  const isAgentPage = location.pathname === "/carlo-agent";

  // Panel state
  const [isOpen, setIsOpen] = useState(false);
  const [hasNewResponse, setHasNewResponse] = useState(false);

  // Conversations
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [showConvoDropdown, setShowConvoDropdown] = useState(false);

  // Messages
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [fileError, setFileError] = useState("");
  const [isDraggingFile, setIsDraggingFile] = useState(false);
  const [showAttachMenu, setShowAttachMenu] = useState(false);

  // Voice
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Refs
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const attachedFilesRef = useRef([]);
  const attachmentMenuRef = useRef(null);
  const dragDepthRef = useRef(0);

  // -----------------------------------------------------------------------
  // Scroll to bottom
  // -----------------------------------------------------------------------
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
      return;
    }
    setIsDraggingFile(false);
    dragDepthRef.current = 0;
  }, [isOpen]);

  useEffect(() => {
    if (!isAgentPage) return;
    setIsOpen(false);
    setHasNewResponse(false);
    setIsDraggingFile(false);
    dragDepthRef.current = 0;
  }, [isAgentPage]);

  // -----------------------------------------------------------------------
  // Load conversations on mount and when restaurant changes
  // -----------------------------------------------------------------------
  const loadConversations = useCallback(async () => {
    try {
      const convos = await base44.agents.listConversations({
        agent_name: "carlo_forecast",
      });
      setConversations(convos || []);
      return convos || [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    loadConversations().then((convos) => {
      if (convos.length > 0 && !currentConversationId) {
        setCurrentConversationId(convos[0].id);
      }
    });
  }, [loadConversations]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // Load messages when switching conversation
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!currentConversationId) {
      setMessages([]);
      return;
    }
    base44.agents
      .getConversation(currentConversationId)
      .then((data) => setMessages(data.messages || []))
      .catch(() => setMessages([]));
  }, [currentConversationId]);

  // -----------------------------------------------------------------------
  // Close dropdown on outside click
  // -----------------------------------------------------------------------
  useEffect(() => {
    function handleClick(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowConvoDropdown(false);
      }
      if (attachmentMenuRef.current && !attachmentMenuRef.current.contains(e.target)) {
        setShowAttachMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  // -----------------------------------------------------------------------
  // File attachments
  // -----------------------------------------------------------------------
  const setAttachmentsFromFiles = useCallback(
    (fileList) => {
      const files = Array.from(fileList || []).filter(Boolean);
      if (!files.length) return false;
      if (attachedFiles.length + files.length > MAX_ATTACHMENTS) {
        setFileError(
          t("carlo.attachmentTooMany", "Attach up to 8 files at once."),
        );
        return false;
      }

      const tooLarge = files.find((file) => file.size > MAX_ATTACHMENT_MB * 1024 * 1024);
      if (tooLarge) {
        setFileError(
          t("carlo.attachmentTooLarge", "File is larger than 15 MB."),
        );
        return false;
      }

      setAttachedFiles((current) => {
        const next = [...current, ...files];
        attachedFilesRef.current = next;
        return next;
      });
      setFileError("");
      return true;
    },
    [attachedFiles.length, t],
  );

  const clearAttachment = useCallback((indexToRemove = null) => {
    setAttachedFiles((current) => {
      const next = indexToRemove === null
        ? []
        : current.filter((_, index) => index !== indexToRemove);
      attachedFilesRef.current = next;
      return next;
    });
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }
  }, []);

  const handleAttachClick = () => {
    setFileError("");
    setShowAttachMenu((current) => !current);
  };

  const openFilePicker = () => {
    setFileError("");
    setShowAttachMenu(false);
    fileInputRef.current?.click();
  };

  const openCameraPicker = () => {
    setFileError("");
    setShowAttachMenu(false);
    cameraInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    setAttachmentsFromFiles(files);
  };

  const hasDraggedFiles = (event) =>
    Array.from(event.dataTransfer?.types || []).includes("Files");

  const handleDragEnter = useCallback((event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current += 1;
    setIsDraggingFile(true);
  }, []);

  const handleDragOver = useCallback((event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
    if (!isDraggingFile) {
      setIsDraggingFile(true);
    }
  }, [isDraggingFile]);

  const handleDragLeave = useCallback((event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
    if (dragDepthRef.current === 0) {
      setIsDraggingFile(false);
    }
  }, []);

  const handleDrop = useCallback((event) => {
    if (!hasDraggedFiles(event)) return;
    event.preventDefault();
    event.stopPropagation();
    dragDepthRef.current = 0;
    setIsDraggingFile(false);
    setAttachmentsFromFiles(event.dataTransfer?.files);
    inputRef.current?.focus();
  }, [setAttachmentsFromFiles]);

  // Paste an image (or any file) straight from the clipboard into the chat input.
  // We try every shape a pasted image can take: blob items, clipboardData.files,
  // and an <img src> in pasted HTML (image copied from a web page). On every paste
  // we surface what we found so the user always gets feedback.
  const renameClipboardFile = useCallback((file) => {
    if (!file) return null;
    if (file.name && file.name !== "image.png") return file;
    const ext = (file.type && file.type.split("/")[1]) || "png";
    const stamp = new Date().toISOString().replace(/[:.]/g, "-");
    return new File([file], `pasted-${stamp}.${ext}`, { type: file.type || "image/png" });
  }, []);

  const handlePaste = useCallback(
    (event) => {
      const clipboard = event.clipboardData || window.clipboardData;
      if (!clipboard) return;

      const items = Array.from(clipboard.items || []);
      const files = [];

      // 1) Direct blobs: screenshots, "Copy image", copied files.
      items.forEach((item) => {
        const looksLikeFile =
          item.kind === "file" || (item.type && item.type.startsWith("image/"));
        if (looksLikeFile && typeof item.getAsFile === "function") {
          const f = renameClipboardFile(item.getAsFile());
          if (f) files.push(f);
        }
      });
      // 2) Browsers that only populate clipboardData.files.
      if (!files.length) {
        Array.from(clipboard.files || []).forEach((file) => {
          const f = renameClipboardFile(file);
          if (f) files.push(f);
        });
      }

      if (files.length) {
        event.preventDefault();
        setFileError("");
        setAttachmentsFromFiles(files);
        inputRef.current?.focus();
        return;
      }

      // 3) Image copied from a web page often arrives only as HTML <img src=...>.
      const html =
        (clipboard.getData && clipboard.getData("text/html")) || "";
      const match = html.match(/<img[^>]+src=["']([^"']+)["']/i);
      if (match && match[1]) {
        event.preventDefault();
        fetch(match[1])
          .then((r) => r.blob())
          .then((blob) => {
            const f = renameClipboardFile(
              new File([blob], "image.png", { type: blob.type || "image/png" }),
            );
            if (f) {
              setFileError("");
              setAttachmentsFromFiles([f]);
              inputRef.current?.focus();
            }
          })
          .catch(() =>
            setFileError(
              t(
                "carlo.pasteImageFailed",
                "Couldn't read that image from the clipboard — try drag & drop instead.",
              ),
            ),
          );
        return;
      }

      // 4) Nothing usable. Give feedback (incl. detected formats so we can debug).
      const types =
        (clipboard.types && Array.from(clipboard.types).join(", ")) || "none";
      if (types !== "none" && types !== "text/plain") {
        setFileError(
          t(
            "carlo.pasteNoImage",
            'No image found on the clipboard. Use "Copy image" (not copy a file), or drag the file into the chat.',
          ) + ` [${types}]`,
        );
      }
    },
    [renameClipboardFile, setAttachmentsFromFiles, t],
  );

  // Listen for paste at the document level while the chat is open, so a copied
  // image attaches even when the textarea isn't the focused element (the usual
  // case right after grabbing a screenshot from another window).
  useEffect(() => {
    if (!isOpen) return undefined;
    document.addEventListener("paste", handlePaste);
    return () => document.removeEventListener("paste", handlePaste);
  }, [isOpen, handlePaste]);

  // -----------------------------------------------------------------------
  // Send message
  // -----------------------------------------------------------------------
  const sendMessage = useCallback(
    async (text) => {
      const nextInput = typeof text === "string" ? text : input;
      const trimmed = nextInput.trim();
      const filesToSend = attachedFilesRef.current;
      if ((!trimmed && filesToSend.length === 0) || isLoading) return;

      setIsLoading(true);
      setInput("");
      attachedFilesRef.current = [];
      setAttachedFiles([]);
      setFileError("");
      setShowAttachMenu(false);
      const attachmentMetadata = filesToSend.map((file) => ({
        filename: file.name,
        size: file.size,
        media_type: file.type || "",
        kind: file.type?.startsWith("image/") ? "image" : undefined,
      }));
      const attachmentNames = filesToSend.map((file) => file.name).join(", ");
      setMessages((prev) => [
        ...prev,
        {
          role: "user",
          content: trimmed || (filesToSend.length ? `(attached ${attachmentNames})` : ""),
          metadata: filesToSend.length
            ? {
                attachment: attachmentMetadata[0],
                attachments: attachmentMetadata,
              }
            : undefined,
        },
      ]);

      try {
        let convoId = currentConversationId;

        // Auto-create conversation if none
        if (!convoId) {
          const conversation = await base44.agents.createConversation({
            agent_name: "carlo_forecast",
            metadata: {
              name: `Carlo ${format(new Date(), "dd.MM.yyyy HH:mm")}`,
            },
          });
          convoId = conversation.id;
          setCurrentConversationId(convoId);
          await loadConversations();
        }

        const result = await base44.agents.addMessage(
          { id: convoId },
          { content: trimmed, files: filesToSend },
        );

        if (result.assistant_message) {
          setMessages((prev) => [...prev, result.assistant_message]);
          if (!isOpen) setHasNewResponse(true);
        }
      } catch {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Sorry, there was an error processing your message. Please try again.",
          },
        ]);
      } finally {
        setIsLoading(false);
      }
    },
    [currentConversationId, input, isLoading, isOpen, loadConversations]
  );

  // -----------------------------------------------------------------------
  // Keyboard handler
  // -----------------------------------------------------------------------
  const handleKeyDown = (e) => {
    if (e.isComposing || e.nativeEvent?.isComposing) {
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // -----------------------------------------------------------------------
  // New conversation
  // -----------------------------------------------------------------------
  const createNewConversation = async () => {
    try {
      const conversation = await base44.agents.createConversation({
        agent_name: "carlo_forecast",
        metadata: {
          name: `Carlo ${format(new Date(), "dd.MM.yyyy HH:mm")}`,
        },
      });
      setCurrentConversationId(conversation.id);
      setMessages([]);
      setShowConvoDropdown(false);
      loadConversations();
    } catch (err) {
      console.error("Error creating conversation:", err);
    }
  };

  // -----------------------------------------------------------------------
  // Voice recording
  // -----------------------------------------------------------------------
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm")
          ? "audio/webm"
          : "audio/mp4",
      });

      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach((track) => track.stop());
        const mimeType = mediaRecorder.mimeType;
        const blob = new Blob(audioChunksRef.current, { type: mimeType });

        if (blob.size < 100) return; // Too short, skip

        setIsTranscribing(true);
        try {
          const result = await base44.agents.transcribe(blob);
          if (result?.text) {
            setInput((prev) => (prev ? prev + " " + result.text : result.text));
            inputRef.current?.focus();
          }
        } catch (err) {
          console.error("Transcription error:", err);
        } finally {
          setIsTranscribing(false);
        }
      };

      mediaRecorderRef.current = mediaRecorder;
      mediaRecorder.start();
      setIsRecording(true);
    } catch (err) {
      console.error("Microphone access error:", err);
      alert(t("carlo.voiceError"));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
  };

  const toggleRecording = () => {
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  // -----------------------------------------------------------------------
  // Toggle panel
  // -----------------------------------------------------------------------
  const togglePanel = () => {
    setIsOpen(!isOpen);
    if (!isOpen) setHasNewResponse(false);
  };

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------
  const restaurantName = currentRestaurant?.name || "Carlo";
  const launcherStyle = {
    position: "fixed",
    right: `${CHAT_EDGE_GAP}px`,
    bottom: `${CHAT_EDGE_GAP}px`,
    left: "auto",
    top: "auto",
    zIndex: 80,
  };
  const panelStyle = {
    position: "fixed",
    right: `${CHAT_EDGE_GAP_MOBILE}px`,
    bottom: `${CHAT_EDGE_GAP_MOBILE}px`,
    left: "auto",
    top: "auto",
    width: `min(400px, calc(100vw - ${CHAT_EDGE_GAP_MOBILE * 2}px))`,
    height: `min(550px, calc(100vh - ${CHAT_EDGE_GAP_MOBILE * 2}px))`,
    maxWidth: `calc(100vw - ${CHAT_EDGE_GAP_MOBILE * 2}px)`,
    maxHeight: `calc(100vh - ${CHAT_EDGE_GAP_MOBILE * 2}px)`,
    zIndex: 80,
  };

  const floatingWidget = (
    <>
      {/* Floating bubble button */}
      {!isOpen && (
        <button
          onClick={togglePanel}
          className={`fixed bottom-6 right-6 left-auto top-auto z-[80] flex h-14 w-14 items-center justify-center rounded-full bg-emerald-700 text-white shadow-lg transition-all duration-200 hover:bg-emerald-800 hover:shadow-xl ${
            hasNewResponse ? "animate-pulse" : ""
          }`}
          style={launcherStyle}
          title="Carlo"
        >
          <Bot className="w-6 h-6" />
        </button>
      )}

      {/* Chat panel */}
      {isOpen && (
        <div
          className="fixed z-[80] relative flex flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
          style={panelStyle}
          onDragEnter={handleDragEnter}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {isDraggingFile && (
            <div className="absolute inset-0 z-20 flex items-center justify-center bg-emerald-950/10 p-4 pointer-events-none">
              <div className="w-full max-w-xs rounded-2xl border-2 border-dashed border-emerald-300 bg-white/95 px-5 py-6 text-center shadow-xl">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700">
                  <FileText className="h-6 w-6" />
                </div>
                <p className="mt-3 text-sm font-semibold text-slate-900">
                  {t("carlo.dropFileHere", "Drop file to attach")}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  {t("carlo.dropFileHint", "Files up to 15 MB")}
                </p>
              </div>
            </div>
          )}
          {/* Header */}
          <div className="bg-emerald-700 text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
              <Bot className="w-4 h-4 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm leading-tight">Carlo</h3>
              <p className="text-xs text-white/70 truncate">{restaurantName}</p>
            </div>
            <div className="flex items-center gap-1">
              <Link
                to="/carlo-agent"
                onClick={() => {
                  setIsOpen(false);
                  setHasNewResponse(false);
                }}
                className="p-1.5 rounded-lg hover:bg-white/15 transition-colors"
                title={t("carlo.expand")}
              >
                <Maximize2 className="w-4 h-4" />
              </Link>
              <button
                onClick={togglePanel}
                className="p-1.5 rounded-lg hover:bg-white/15 transition-colors"
                title={t("carlo.close")}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Conversation selector */}
          <div className="px-3 py-2 border-b border-slate-100 flex items-center gap-2 flex-shrink-0 bg-slate-50/50">
            <div className="relative flex-1" ref={dropdownRef}>
              <button
                onClick={() => setShowConvoDropdown(!showConvoDropdown)}
                className="w-full text-left text-xs px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors flex items-center justify-between"
              >
                <span className="truncate text-slate-600">
                  {conversations.find((c) => c.id === currentConversationId)
                    ?.metadata?.name ||
                    t("carlo.newConversation")}
                </span>
                <ChevronDown className="w-3 h-3 text-slate-400 flex-shrink-0 ml-1" />
              </button>

              {showConvoDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg z-10 max-h-48 overflow-y-auto">
                  {conversations.slice(0, 10).map((convo) => (
                    <button
                      key={convo.id}
                      onClick={() => {
                        setCurrentConversationId(convo.id);
                        setShowConvoDropdown(false);
                      }}
                      className={`w-full text-left text-xs px-3 py-2 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0 ${
                        convo.id === currentConversationId
                          ? "bg-emerald-50 text-emerald-800"
                          : "text-slate-600"
                      }`}
                    >
                      <span className="block truncate font-medium">
                        {convo.metadata?.name || "Carlo Session"}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {format(new Date(convo.created_date), "dd.MM.yyyy HH:mm")}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={createNewConversation}
              className="p-1.5 rounded-lg border border-slate-200 bg-white hover:bg-slate-50 transition-colors"
              title={t("carlo.newConversation")}
            >
              <Plus className="w-3.5 h-3.5 text-slate-500" />
            </button>
          </div>

          {/* Messages area */}
          <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3">
            {messages.length === 0 && !isLoading ? (
              <div className="flex flex-col items-center justify-center h-full py-6">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mb-4">
                  <Bot className="w-7 h-7 text-emerald-700" />
                </div>
                <p className="text-sm font-medium text-slate-700 mb-1">
                  {t("carlo.title")}
                </p>
                <p className="text-xs text-slate-500 text-center mb-4 px-4">
                  {t("carlo.placeholder")}
                </p>
                <QuickActions
                  onSelect={(prompt) => sendMessage(prompt)}
                  t={t}
                />
              </div>
            ) : (
              messages.map((msg, idx) => (
                <MessageBubble key={idx} message={msg} t={t} />
              ))
            )}

            {isLoading && (
              <div className="flex gap-2.5 justify-start">
                <div className="h-7 w-7 rounded-lg bg-emerald-700 flex items-center justify-center flex-shrink-0">
                  <Bot className="w-3.5 h-3.5 text-white" />
                </div>
                <div className="bg-white border border-slate-200 rounded-2xl px-3.5 py-2.5 shadow-sm">
                  <div className="flex items-center gap-2">
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-600" />
                    <span className="text-xs text-slate-500">
                      {t("carlo.thinking")}
                    </span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Input area */}
          <div className="border-t border-slate-200 bg-white px-3 py-2.5 flex-shrink-0">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={handleFileChange}
            />

            {fileError && (
              <p className="mb-2 px-1 text-xs text-red-600">{fileError}</p>
            )}

            <div className="flex items-end gap-2">
              <div className="relative flex-shrink-0" ref={attachmentMenuRef}>
                <button
                  type="button"
                  onClick={handleAttachClick}
                  disabled={isLoading || isTranscribing}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-600 transition-colors hover:bg-slate-200 disabled:opacity-50"
                  aria-label={t("carlo.attachFile", "Attach file")}
                  title={t("carlo.attachFile", "Attach file")}
                >
                  <Plus className="h-6 w-6" />
                </button>
                {showAttachMenu && (
                  <div className="absolute bottom-full left-0 z-30 mb-2 w-36 rounded-xl border border-slate-200 bg-white p-1 shadow-lg">
                    <button
                      type="button"
                      onClick={openFilePicker}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <FileText className="h-4 w-4 text-slate-500" />
                      {t("carlo.attachFiles", "Files")}
                    </button>
                    <button
                      type="button"
                      onClick={openCameraPicker}
                      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                    >
                      <Camera className="h-4 w-4 text-slate-500" />
                      {t("carlo.attachCamera", "Camera")}
                    </button>
                  </div>
                )}
              </div>

              <div className="relative flex-1 rounded-xl border border-slate-200 bg-slate-50 transition-colors focus-within:border-emerald-400 focus-within:bg-white focus-within:ring-1 focus-within:ring-emerald-400">
                {attachedFiles.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 px-2.5 pt-2.5">
                    {attachedFiles.map((file, index) => {
                      const AttachmentIcon = file.type?.startsWith("image/") ? ImageIcon : FileText;
                      return (
                        <div
                          key={`${file.name}-${file.size}-${index}`}
                          className="inline-flex max-w-full items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-800"
                        >
                          <AttachmentIcon className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="max-w-[180px] truncate font-medium">{file.name}</span>
                          <button
                            type="button"
                            onClick={() => clearAttachment(index)}
                            className="rounded-full p-0.5 hover:bg-emerald-100"
                            aria-label={t("carlo.removeAttachment", "Remove attachment")}
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
                <textarea
                  ref={inputRef}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={
                    attachedFiles.length
                      ? t("carlo.addMessageWithFile", "Add a message")
                      : t("carlo.placeholder")
                  }
                  disabled={isLoading}
                  rows={1}
                  className="w-full resize-none rounded-xl border-0 bg-transparent px-3 py-2 text-sm outline-none placeholder:text-slate-400 disabled:opacity-50"
                  style={{
                    minHeight: "38px",
                    maxHeight: "96px",
                    height: "auto",
                    overflowY: input.split("\n").length > 3 ? "auto" : "hidden",
                  }}
                  onInput={(e) => {
                    e.target.style.height = "auto";
                    e.target.style.height =
                      Math.min(e.target.scrollHeight, 96) + "px";
                  }}
                />
              </div>

              {/* Mic button */}
              <button
                onClick={toggleRecording}
                disabled={isLoading || isTranscribing}
                className={`h-11 w-11 flex-shrink-0 rounded-2xl p-2 transition-colors ${
                  isRecording
                    ? "bg-red-500 text-white hover:bg-red-600"
                    : isTranscribing
                    ? "bg-amber-100 text-amber-600"
                    : "bg-slate-100 text-slate-500 hover:bg-slate-200"
                }`}
                aria-label={t("carlo.voiceInput", "Voice input")}
                title={
                  isRecording
                    ? t("carlo.recording")
                    : isTranscribing
                    ? t("carlo.transcribing")
                    : "Voice"
                }
              >
                {isTranscribing ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : isRecording ? (
                  <MicOff className="w-4 h-4" />
                ) : (
                  <Mic className="w-4 h-4" />
                )}
              </button>

              {/* Send button */}
              <Button
                onClick={() => sendMessage()}
                disabled={isLoading || (!input.trim() && attachedFiles.length === 0)}
                className="h-11 flex-shrink-0 rounded-2xl bg-emerald-700 px-3 hover:bg-emerald-800"
                aria-label={t("carlo.send", "Send")}
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(floatingWidget, document.body);
}
